import React, { useCallback, useMemo } from 'react';
import { CustomTableCell } from '../../../components/customTable';
import { StyledTableRow } from './StyledTableRow';
import { Invoice } from '../../../redux/invoices';
import { Box, Theme, Typography } from '@mui/material';
import { priceFormat } from '../../../components/tableCellRenders';
import { getDateDifference } from '../../../utils';

export const InvoiceDataRow = ({ data }: { data: Invoice }) => {
  const columnWidth = (theme: Theme) => theme.spacing(15);

  const numberOfDueDays = useMemo(() => {
    if (!data.isPaidAfterPastDue || !data.paidOn) {
      return '—';
    }
    return getDateDifference(
      new Date(data.invoiceDueDate),
      new Date(data.paidOn),
    );
  }, [data]);

  const openInvoiceDocument = useCallback((fileId?: string) => {
    if (!fileId) return;
    window.open(
      `https://drive.google.com/file/d/${fileId}/edit`,
      '_blank',
      'noopener,noreferrer',
    );
  }, []);

  return (
    <StyledTableRow isLastRow={false} key={data.id}>
      <CustomTableCell
        sx={{
          borderBottom: '0',
          minWidth: columnWidth,
          py: 1,
          '&:hover': {
            color: (theme) =>
              data.invoiceUrl ? theme.palette.primary.main : '',
          },
          cursor: data.invoiceUrl ? 'pointer' : '',
          textDecoration: data.invoiceUrl ? 'underline' : '',
        }}
        pure
        nowrap
        onClick={() => openInvoiceDocument(data?.invoiceUrl)}
      >
        {data.id}
      </CustomTableCell>
      <CustomTableCell
        sx={{ borderBottom: '0', minWidth: columnWidth, py: 1 }}
        pure
        nowrap
      >
        <Box>{data.projectName ? data.projectName : '—'}</Box>
      </CustomTableCell>
      <CustomTableCell
        sx={{ borderBottom: '0', minWidth: columnWidth, py: 1 }}
        pure
        nowrap
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="end"
          justifyContent="end"
        >
          <Typography
            align={'right'}
            sx={{ fontWeight: '500', marginRight: '-20px' }}
          >
            {priceFormat({
              value: data.amount ? Math.round(data.amount) : data.amount,
            })}
          </Typography>
        </Box>
      </CustomTableCell>

      <CustomTableCell
        sx={{ borderBottom: '0', minWidth: columnWidth, py: 1 }}
        pure
        nowrap
      >
        <Box>
          <Typography whiteSpace="nowrap" variant="body1">
            {new Date(data.invoiceDueDate).toLocaleDateString('en-us')}
          </Typography>
        </Box>
      </CustomTableCell>

      {/* Paid on */}
      <CustomTableCell
        sx={{ borderBottom: '0', minWidth: columnWidth, py: 1 }}
        pure
        nowrap
      >
        <Box>
          <Typography
            whiteSpace="nowrap"
            variant="body2"
            color={data.isPaidAfterPastDue ? 'red' : 'black'}
          >
            {data.paidOn
              ? new Date(data.paidOn).toLocaleDateString('en-us')
              : '—'}
          </Typography>
        </Box>
      </CustomTableCell>

      {/* Paid after past due */}
      <CustomTableCell
        sx={{ borderBottom: '0', minWidth: columnWidth, py: 1 }}
        pure
        nowrap
      >
        <Box>
          <Typography whiteSpace="nowrap" variant="body1">
            {numberOfDueDays}
          </Typography>
        </Box>
      </CustomTableCell>
    </StyledTableRow>
  );
};
